import { Link } from "react-router-dom";
import { ArrowUpRight, GraduationCap, Trophy } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { CLUBS, IMAGES } from "@/data/content";

export default function EnglishIsLekka() {
  return (
    <div data-testid="lekka-overview-page">
      <section className="relative overflow-hidden bg-sand grain py-16 sm:py-20">
        <img
          src={IMAGES.excursions}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover opacity-15"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-sand/30 via-sand/70 to-sand" />
        <div className="relative mx-auto max-w-5xl px-6">
          <Reveal>
            <Eyebrow>Flagship programme, since 2022</Eyebrow>
            <h1 className="mt-5 font-display text-5xl font-extrabold tracking-tight text-coal sm:text-6xl lg:text-7xl">
              English is <span className="text-ember">Lekka</span>
            </h1>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-7 max-w-3xl text-base leading-relaxed text-coal-muted md:text-lg">
              A transformative literacy and social justice programme designed to do more than improve
              grades. Through reading, writing, debating and filmmaking clubs, competitive showcases,
              museum and theatre excursions, and targeted Grade 12 academic interventions, we build
              learners who are articulate, curious, and equipped to navigate the world with
              confidence.
            </p>
            <p className="mt-5 max-w-3xl text-base leading-relaxed text-coal-muted">
              Local communities are engaged, annual celebrations with notable speakers are hosted,
              and learners are guided toward creative professions. From our first competition at the
              Labia Theatre in 2022 to a leadership camp on Robben Island in 2025, English is Lekka
              has consistently expanded what learners believe is possible for themselves.
            </p>
          </Reveal>
        </div>
      </section>
      <section className="py-16" data-testid="clubs-grid-section">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <Eyebrow>Four clubs</Eyebrow>
            <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-coal sm:text-5xl">
              Choose your club
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2">
            {Object.values(CLUBS).map((club, i) => (
              <Reveal key={club.slug} delay={i * 0.08}>
                <Link
                  to={`/english-is-lekka/clubs/${club.slug}`}
                  data-testid={`club-card-${club.slug}`}
                  className="group relative block overflow-hidden rounded-[2rem]"
                >
                  <img
                    src={club.image}
                    alt={club.name}
                    className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-coal-dark/90 via-coal-dark/30 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-7">
                    <span className="font-mono-brand text-[11px] font-bold uppercase tracking-[0.25em] text-ember-light">
                      {club.tagline}
                    </span>
                    <div className="mt-1.5 flex items-center justify-between">
                      <h3 className="font-display text-2xl font-extrabold text-sand sm:text-3xl">{club.name}</h3>
                      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-ember text-sand transition-transform duration-300 group-hover:rotate-45">
                        <ArrowUpRight size={18} />
                      </span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-teal-subtle py-14" data-testid="workshops-promo">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <Link
              to="/english-is-lekka/workshops"
              data-testid="workshops-promo-link"
              className="group flex flex-col items-start justify-between gap-6 rounded-[2rem] border border-teal/15 bg-white p-8 shadow-[0_16px_44px_-22px_rgba(26,29,36,0.3)] transition-shadow hover:shadow-[0_24px_60px_-24px_rgba(15,118,110,0.45)] sm:flex-row sm:items-center"
            >
              <div className="flex items-start gap-5">
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-teal text-sand">
                  <GraduationCap size={26} />
                </span>
                <div>
                  <Eyebrow>Teacher development</Eyebrow>
                  <h2 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-coal sm:text-4xl">
                    Workshops behind the classroom magic
                  </h2>
                  <p className="mt-2 max-w-xl text-sm leading-relaxed text-coal-muted">
                    Standard setting, poetry, marking practice and visual literacy, over 1,100
                    teacher training seats since 2022.
                  </p>
                </div>
              </div>
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-ember text-sand transition-transform duration-300 group-hover:rotate-45">
                <ArrowUpRight size={20} />
              </span>
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="bg-ember-subtle py-14" data-testid="winner-spotlight">
        <div className="mx-auto flex max-w-5xl flex-col items-center gap-6 px-6 text-center">
          <Reveal>
            <img
              src={CLUBS.writing.winner.photo}
              alt="Alizwa Pikoli, English is Lekka's 2024 winner"
              className="h-28 w-28 rounded-full border-4 border-ember/30 object-cover shadow-[0_14px_40px_-12px_rgba(217,93,57,0.7)]"
              data-testid="winner-photo"
            />
            <p className="mt-6 font-mono-brand text-xs font-bold uppercase tracking-[0.3em] text-ember">
              Spotlight
            </p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-coal sm:text-4xl">
              Alizwa Pikoli
            </h2>
            <p className="mt-2 font-heading text-lg font-bold text-teal">
              English is Lekka's 2024 Winner, Immaculate High School
            </p>
            <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-coal-muted sm:text-base">
              Every year, one learner's words rise above the rest. Alizwa set the standard, who
              takes the crown next?
            </p>
            <Link
              to="/english-is-lekka/clubs/writing"
              data-testid="winner-writing-link"
              className="mt-2 inline-flex items-center gap-2 rounded-full bg-coal px-6 py-2.5 font-heading text-sm font-bold text-sand transition-colors hover:bg-teal"
            >
              Meet the Writing Club
              <ArrowUpRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
